import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Camera, CheckCircle, AlertTriangle, ArrowLeft, Settings, RotateCcw, Phone } from "lucide-react";
import Tesseract from "tesseract.js";
import { KioskViewport } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const STORAGE_KEY_FRONT = 'parkwise_front_camera_id';
const STORAGE_KEY_REAR = 'parkwise_rear_camera_id';
const IDLE_TIMEOUT = 60;

export default function KioskBadgeScan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationData } = location.state || {};

  // Camera configuration state
  const [configMode, setConfigMode] = useState(false);
  const [devices, setDevices] = useState([]);
  const [frontCameraId, setFrontCameraId] = useState(() => localStorage.getItem(STORAGE_KEY_FRONT) || '');
  const [rearCameraId, setRearCameraId] = useState(() => localStorage.getItem(STORAGE_KEY_REAR) || '');

  // Stream state
  const [enumComplete, setEnumComplete] = useState(false); // Prevents race condition
  const [streamsReady, setStreamsReady] = useState(false);
  const [frontStatus, setFrontStatus] = useState('idle'); // idle, opening, open, error
  const [rearStatus, setRearStatus] = useState('idle');
  const [frontError, setFrontError] = useState(null);
  const [rearError, setRearError] = useState(null);

  // Capture state
  const [frontImage, setFrontImage] = useState(null);
  const [rearImage, setRearImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);

  // Refs
  const frontVideoRef = useRef(null);
  const rearVideoRef = useRef(null);
  const frontStreamRef = useRef(null);
  const rearStreamRef = useRef(null);
  const frontCanvasRef = useRef(null);
  const rearCanvasRef = useRef(null);
  const idleTimerRef = useRef(null);

  // Idle timeout - return to attract after 60s inactivity (paused during active scan/processing)
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      // Only set timeout if not actively processing
      if (!isProcessing) {
        idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
      }
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate, isProcessing]);

  // Enumerate available cameras
  const enumerateCameras = useCallback(async () => {
    console.log('[DUAL-CAM] Enumerating cameras...');
    setEnumComplete(false); // Mark enumeration in progress
    
    // Step 1: Enumerate FIRST (works without permission, returns empty labels)
    let allDevices = await navigator.mediaDevices.enumerateDevices();
    let videoDevices = allDevices.filter(d => d.kind === 'videoinput');
    console.log('[DUAL-CAM] Initial enumeration: ' + videoDevices.length + ' cameras (labels may be empty)');

    // Step 2: If devices found but no labels, request permission
    if (videoDevices.length > 0 && !videoDevices[0].label) {
      console.log('[DUAL-CAM] Requesting camera permission...');
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        tempStream.getTracks().forEach(t => t.stop());
        
        // CRITICAL: Wait for USB handle release on Windows
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('[DUAL-CAM] Permission granted, re-enumerating...');
        
        // Re-enumerate to get labels
        allDevices = await navigator.mediaDevices.enumerateDevices();
        videoDevices = allDevices.filter(d => d.kind === 'videoinput');
      } catch (err) {
        console.warn('[DUAL-CAM] Permission request failed:', err.name, err.message);
        // Continue with empty labels - devices still usable
      }
    }

    console.log('[DUAL-CAM] Final count: ' + videoDevices.length + ' cameras');
    videoDevices.forEach((d, i) => {
      console.log('[DUAL-CAM]   [' + i + '] ' + (d.label || 'Camera ' + i) + ' | ID: ' + (d.deviceId ? d.deviceId.substring(0, 16) + '...' : '(empty)'));
    });
    
    setDevices(videoDevices);
    setEnumComplete(true); // Mark enumeration complete
    return videoDevices;
  }, []);

  // Check if cameras are configured
  const camerasConfigured = frontCameraId && rearCameraId && frontCameraId !== rearCameraId;

  // Open BOTH camera streams SIMULTANEOUSLY
  const openBothStreams = useCallback(async () => {
    if (!camerasConfigured) {
      console.error('[DUAL-CAM] Cameras not configured');
      return;
    }

    console.log('[DUAL-CAM] ========================================');
    console.log('[DUAL-CAM] OPENING BOTH STREAMS SIMULTANEOUSLY');
    console.log('[DUAL-CAM] Front Camera ID: ' + frontCameraId.substring(0, 16) + '...');
    console.log('[DUAL-CAM] Rear Camera ID: ' + rearCameraId.substring(0, 16) + '...');
    console.log('[DUAL-CAM] ========================================');

    setFrontStatus('opening');
    setRearStatus('opening');
    setFrontError(null);
    setRearError(null);
    setStreamsReady(false);

    // Create both getUserMedia promises
    const frontPromise = navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: { exact: frontCameraId },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    });

    const rearPromise = navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: { exact: rearCameraId },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    });

    // Execute BOTH in parallel using Promise.allSettled
    const results = await Promise.allSettled([frontPromise, rearPromise]);

    const [frontResult, rearResult] = results;

    // Handle front camera result
    if (frontResult.status === 'fulfilled') {
      frontStreamRef.current = frontResult.value;
      if (frontVideoRef.current) {
        frontVideoRef.current.srcObject = frontResult.value;
      }
      setFrontStatus('open');
      console.log('[DUAL-CAM] Front camera OPEN - Track: ' + frontResult.value.getVideoTracks()[0]?.label);
    } else {
      setFrontStatus('error');
      setFrontError(frontResult.reason?.name + ': ' + frontResult.reason?.message);
      console.error('[DUAL-CAM] Front camera FAILED:', frontResult.reason);
    }

    // Handle rear camera result
    if (rearResult.status === 'fulfilled') {
      rearStreamRef.current = rearResult.value;
      if (rearVideoRef.current) {
        rearVideoRef.current.srcObject = rearResult.value;
      }
      setRearStatus('open');
      console.log('[DUAL-CAM] Rear camera OPEN - Track: ' + rearResult.value.getVideoTracks()[0]?.label);
    } else {
      setRearStatus('error');
      setRearError(rearResult.reason?.name + ': ' + rearResult.reason?.message);
      console.error('[DUAL-CAM] Rear camera FAILED:', rearResult.reason);
    }

    // Both must succeed
    if (frontResult.status === 'fulfilled' && rearResult.status === 'fulfilled') {
      setStreamsReady(true);
      console.log('[DUAL-CAM] ========================================');
      console.log('[DUAL-CAM] BOTH STREAMS OPEN - READY FOR CAPTURE');
      console.log('[DUAL-CAM] ========================================');
    } else {
      console.error('[DUAL-CAM] ========================================');
      console.error('[DUAL-CAM] STREAM INITIALIZATION FAILED');
      console.error('[DUAL-CAM] ========================================');
    }
  }, [frontCameraId, rearCameraId, camerasConfigured]);

  // Stop all streams
  const stopAllStreams = useCallback(() => {
    console.log('[DUAL-CAM] Stopping all streams');
    if (frontStreamRef.current) {
      frontStreamRef.current.getTracks().forEach(t => t.stop());
      frontStreamRef.current = null;
    }
    if (rearStreamRef.current) {
      rearStreamRef.current.getTracks().forEach(t => t.stop());
      rearStreamRef.current = null;
    }
    if (frontVideoRef.current) frontVideoRef.current.srcObject = null;
    if (rearVideoRef.current) rearVideoRef.current.srcObject = null;
    setFrontStatus('idle');
    setRearStatus('idle');
    setStreamsReady(false);
  }, []);

  // Capture frame from video to canvas
  const captureFrame = (videoRef, canvasRef) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.9);
  };

  // Capture both images simultaneously
  const captureBothImages = () => {
    console.log('[DUAL-CAM] Capturing both images...');
    
    const front = captureFrame(frontVideoRef, frontCanvasRef);
    const rear = captureFrame(rearVideoRef, rearCanvasRef);

    if (front) {
      setFrontImage(front);
      console.log('[DUAL-CAM] Front image captured (' + Math.round(front.length / 1024) + ' KB)');
    }
    if (rear) {
      setRearImage(rear);
      console.log('[DUAL-CAM] Rear image captured (' + Math.round(rear.length / 1024) + ' KB)');
    }

    return { front, rear };
  };

  // Run OCR on captured images
  const runOCR = async (frontImg, rearImg) => {
    console.log('[DUAL-CAM] Starting OCR processing...');
    setIsProcessing(true);
    setOcrProgress(0);

    let ocrData = {
      holder_name: '',
      badge_number: '',
      expiry_date: '',
      issuing_authority: '',
      confidence: 0
    };

    try {
      // Process rear image first (usually has badge number/expiry)
      if (rearImg) {
        console.log('[DUAL-CAM] Processing rear image with Tesseract...');
        const rearResult = await Tesseract.recognize(rearImg, 'eng', {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              setOcrProgress(Math.round(m.progress * 50));
            }
          }
        });

        const rearText = rearResult.data.text;
        console.log('[DUAL-CAM] Rear OCR text:', rearText.substring(0, 200) + '...');

        // Extract badge number (format: XX12345 or similar)
        const badgeMatch = rearText.match(/\b([A-Z]{1,3}\d{4,8})\b/i);
        if (badgeMatch) ocrData.badge_number = badgeMatch[1].toUpperCase();

        // Extract expiry date (various formats)
        const dateMatch = rearText.match(/(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})/);
        if (dateMatch) ocrData.expiry_date = dateMatch[1];

        // Extract issuing authority
        const authorityMatch = rearText.match(/(Council|Authority|Borough|City of [A-Za-z]+)/i);
        if (authorityMatch) ocrData.issuing_authority = authorityMatch[1];

        ocrData.confidence = rearResult.data.confidence / 100;
      }

      // Process front image (usually has holder name)
      if (frontImg) {
        console.log('[DUAL-CAM] Processing front image with Tesseract...');
        const frontResult = await Tesseract.recognize(frontImg, 'eng', {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              setOcrProgress(50 + Math.round(m.progress * 50));
            }
          }
        });

        const frontText = frontResult.data.text;
        console.log('[DUAL-CAM] Front OCR text:', frontText.substring(0, 200) + '...');

        // Extract holder name (lines with mostly letters, 2+ words)
        const lines = frontText.split('\n').filter(l => l.trim().length > 3);
        for (const line of lines) {
          const cleaned = line.trim();
          if (/^[A-Za-z\s\-\']+$/.test(cleaned) && cleaned.split(/\s+/).length >= 2) {
            ocrData.holder_name = cleaned;
            break;
          }
        }

        // Average confidence
        ocrData.confidence = (ocrData.confidence + frontResult.data.confidence / 100) / 2;
      }

      console.log('[DUAL-CAM] OCR complete:', ocrData);
    } catch (err) {
      console.error('[DUAL-CAM] OCR error:', err);
    }

    setIsProcessing(false);
    setOcrProgress(100);
    return ocrData;
  };

  // Handle capture and process
  const handleCaptureAndProcess = async () => {
    const { front, rear } = captureBothImages();
    
    if (!front || !rear) {
      console.error('[DUAL-CAM] Failed to capture one or both images');
      return;
    }

    // Stop streams after capture
    stopAllStreams();

    // Run OCR
    const ocrData = await runOCR(front, rear);

    // Navigate to review
    navigate('/kiosk/badge-review', {
      state: {
        registrationData,
        frontImage: front,
        rearImage: rear,
        ocrData
      }
    });
  };

  // Save camera configuration
  const saveCameraConfig = (front, rear) => {
    localStorage.setItem(STORAGE_KEY_FRONT, front);
    localStorage.setItem(STORAGE_KEY_REAR, rear);
    setFrontCameraId(front);
    setRearCameraId(rear);
    console.log('[DUAL-CAM] Camera config saved');
  };

  // Initialize on mount
  useEffect(() => {
    enumerateCameras().then((cams) => {
      // Check if stored cameras still exist
      if (cams.length >= 2) {
        const storedFront = localStorage.getItem(STORAGE_KEY_FRONT);
        const storedRear = localStorage.getItem(STORAGE_KEY_REAR);
        
        const frontExists = cams.some(c => c.deviceId === storedFront);
        const rearExists = cams.some(c => c.deviceId === storedRear);
        
        if (!frontExists || !rearExists || storedFront === storedRear) {
          // Need configuration
          setConfigMode(true);
        }
      } else if (cams.length < 2) {
        // Not enough cameras
        setConfigMode(true);
      }
    });

    return () => {
      stopAllStreams();
    };
  }, [enumerateCameras, stopAllStreams]);

  // Auto-open streams when configured and not in config mode
  // CRITICAL: Only after enumeration complete to prevent race condition
  useEffect(() => {
    if (enumComplete && camerasConfigured && !configMode && !streamsReady && frontStatus === 'idle') {
      openBothStreams();
    }
  }, [enumComplete, camerasConfigured, configMode, streamsReady, frontStatus, openBothStreams]);

  // Redirect if no registration data
  useEffect(() => {
    if (!registrationData) {
      navigate('/kiosk/category');
    }
  }, [registrationData, navigate]);

  // Config Mode UI
  if (configMode) {
    return (
      <KioskViewport hideNav>
        <div style={{
          width: '100%', height: '100%', background: '#f8fafc',
          display: 'flex', flexDirection: 'column', fontFamily: "'Inter', sans-serif"
        }}>
          {/* Header */}
          <header style={{
            padding: '24px 40px', background: 'white',
            borderBottom: '2px solid #e2e8f0'
          }}>
            <h1 style={{ fontSize: '36px', fontWeight: '800', color: '#1e3a5f', margin: 0 }}>
              <Settings size={36} style={{ verticalAlign: 'middle', marginRight: '16px' }} />
              Camera Configuration
            </h1>
            <p style={{ fontSize: '18px', color: '#64748b', marginTop: '8px' }}>
              Assign which camera captures the front and rear of the badge
            </p>
          </header>

          {/* Camera List */}
          <div style={{ flex: 1, padding: '40px', overflowY: 'auto' }}>
            {devices.length < 2 ? (
              <div style={{
                padding: '40px', background: '#fef2f2', borderRadius: '16px',
                border: '3px solid #ef4444', textAlign: 'center'
              }}>
                <AlertTriangle size={64} color="#ef4444" />
                <h2 style={{ fontSize: '28px', color: '#dc2626', marginTop: '16px' }}>
                  Insufficient Cameras Detected
                </h2>
                <p style={{ fontSize: '18px', color: '#7f1d1d' }}>
                  Found {devices.length} camera(s). This system requires 2 cameras.
                </p>
                <button
                  onClick={() => enumerateCameras()}
                  style={{
                    marginTop: '24px', padding: '16px 32px', fontSize: '20px', fontWeight: '700',
                    background: '#3b82f6', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer'
                  }}
                >
                  <RotateCcw size={24} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Refresh
                </button>
              </div>
            ) : (
              <>
                <p style={{ fontSize: '20px', color: '#1e3a5f', marginBottom: '24px', fontWeight: '600' }}>
                  Select cameras for each side of the badge:
                </p>

                {/* Front Camera Selection */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{ fontSize: '22px', fontWeight: '700', color: '#1e3a5f', display: 'block', marginBottom: '12px' }}>
                    Front Camera (Photo side of badge)
                  </label>
                  <select
                    value={frontCameraId}
                    onChange={(e) => setFrontCameraId(e.target.value)}
                    style={{
                      width: '100%', padding: '20px', fontSize: '20px', borderRadius: '12px',
                      border: '3px solid #3b82f6', background: 'white'
                    }}
                  >
                    <option value="">-- Select Front Camera --</option>
                    {devices.map((d, i) => (
                      <option key={d.deviceId} value={d.deviceId} disabled={d.deviceId === rearCameraId}>
                        Camera {i + 1}: {d.label || 'Unnamed Camera'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Rear Camera Selection */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{ fontSize: '22px', fontWeight: '700', color: '#1e3a5f', display: 'block', marginBottom: '12px' }}>
                    Rear Camera (Details side of badge)
                  </label>
                  <select
                    value={rearCameraId}
                    onChange={(e) => setRearCameraId(e.target.value)}
                    style={{
                      width: '100%', padding: '20px', fontSize: '20px', borderRadius: '12px',
                      border: '3px solid #22c55e', background: 'white'
                    }}
                  >
                    <option value="">-- Select Rear Camera --</option>
                    {devices.map((d, i) => (
                      <option key={d.deviceId} value={d.deviceId} disabled={d.deviceId === frontCameraId}>
                        Camera {i + 1}: {d.label || 'Unnamed Camera'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Save Button */}
                <button
                  onClick={() => {
                    saveCameraConfig(frontCameraId, rearCameraId);
                    setConfigMode(false);
                  }}
                  disabled={!frontCameraId || !rearCameraId || frontCameraId === rearCameraId}
                  style={{
                    width: '100%', padding: '24px', fontSize: '24px', fontWeight: '700',
                    background: (!frontCameraId || !rearCameraId || frontCameraId === rearCameraId) ? '#94a3b8' : '#22c55e',
                    color: 'white', border: 'none', borderRadius: '16px',
                    cursor: (!frontCameraId || !rearCameraId || frontCameraId === rearCameraId) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <CheckCircle size={28} style={{ verticalAlign: 'middle', marginRight: '12px' }} />
                  Save Configuration
                </button>
              </>
            )}
          </div>
        </div>
      </KioskViewport>
    );
  }

  // Main Capture UI
  return (
    <KioskViewport>
      <div
        data-testid="kiosk-badge-scan"
        style={{
          width: '100%', height: '100%',
          display: 'flex', flexDirection: 'column',
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
          overflow: 'hidden', background: '#ffffff'
        }}
      >
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 40px', background: 'white', flexShrink: 0
        }}>
          <img src="/parkwise-logo.png" alt="ParkWise" style={{ height: '60px', objectFit: 'contain' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Camera size={32} color="#3b82f6" />
            <span style={{ fontSize: '28px', fontWeight: '700', color: '#1e3a5f' }}>Scan Blue Badge</span>
          </div>
          <button
            onClick={() => { stopAllStreams(); setConfigMode(true); }}
            style={{
              padding: '12px 16px', borderRadius: '8px', background: '#f1f5f9',
              border: 'none', cursor: 'pointer'
            }}
          >
            <Settings size={24} color="#64748b" />
          </button>
        </header>

        {/* Instructions */}
        <div style={{
          padding: '24px 40px', background: '#e0f2fe', textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1e3a5f', margin: 0 }}>
            Position your Blue Badge in view of both cameras
          </h2>
          <p style={{ fontSize: '18px', color: '#0369a1', marginTop: '8px' }}>
            Place the badge flat with the photo side facing the left camera
          </p>
        </div>

        {/* Dual Camera Preview */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', gap: '24px',
          padding: '24px 40px', background: 'linear-gradient(180deg, #f8fafc 0%, #e0f2fe 100%)'
        }}>
          <div style={{ display: 'flex', gap: '24px', height: '380px' }}>
            {/* Front Camera */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px'
              }}>
                <div style={{
                  width: '16px', height: '16px', borderRadius: '50%',
                  background: frontStatus === 'open' ? '#22c55e' : frontStatus === 'error' ? '#ef4444' : '#f59e0b'
                }} />
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#1e3a5f' }}>
                  Front Camera (Photo Side)
                </span>
              </div>
              <div style={{
                flex: 1, borderRadius: '16px', overflow: 'hidden', background: '#000',
                border: frontStatus === 'open' ? '4px solid #22c55e' : frontStatus === 'error' ? '4px solid #ef4444' : '4px solid #3b82f6',
                position: 'relative'
              }}>
                <video
                  ref={frontVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {frontStatus === 'opening' && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '20px'
                  }}>
                    Opening camera...
                  </div>
                )}
                {frontStatus === 'error' && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.9)', padding: '20px'
                  }}>
                    <AlertTriangle size={48} color="#ef4444" />
                    <p style={{ color: '#ef4444', fontSize: '16px', textAlign: 'center', marginTop: '12px' }}>
                      {frontError}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Rear Camera */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px'
              }}>
                <div style={{
                  width: '16px', height: '16px', borderRadius: '50%',
                  background: rearStatus === 'open' ? '#22c55e' : rearStatus === 'error' ? '#ef4444' : '#f59e0b'
                }} />
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#1e3a5f' }}>
                  Rear Camera (Details Side)
                </span>
              </div>
              <div style={{
                flex: 1, borderRadius: '16px', overflow: 'hidden', background: '#000',
                border: rearStatus === 'open' ? '4px solid #22c55e' : rearStatus === 'error' ? '4px solid #ef4444' : '4px solid #3b82f6',
                position: 'relative'
              }}>
                <video
                  ref={rearVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {rearStatus === 'opening' && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '20px'
                  }}>
                    Opening camera...
                  </div>
                )}
                {rearStatus === 'error' && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.9)', padding: '20px'
                  }}>
                    <AlertTriangle size={48} color="#ef4444" />
                    <p style={{ color: '#ef4444', fontSize: '16px', textAlign: 'center', marginTop: '12px' }}>
                      {rearError}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status Indicator */}
          <div style={{
            padding: '16px 24px', borderRadius: '12px',
            background: streamsReady ? '#dcfce7' : (frontStatus === 'error' || rearStatus === 'error') ? '#fef2f2' : '#fef9c3',
            border: `3px solid ${streamsReady ? '#22c55e' : (frontStatus === 'error' || rearStatus === 'error') ? '#ef4444' : '#f59e0b'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px'
          }}>
            {streamsReady ? (
              <>
                <CheckCircle size={28} color="#22c55e" />
                <span style={{ fontSize: '22px', fontWeight: '700', color: '#166534' }}>
                  Both cameras ready - Position badge and capture
                </span>
              </>
            ) : (frontStatus === 'error' || rearStatus === 'error') ? (
              <>
                <AlertTriangle size={28} color="#ef4444" />
                <span style={{ fontSize: '22px', fontWeight: '700', color: '#dc2626' }}>
                  Camera error - Check configuration
                </span>
              </>
            ) : (
              <>
                <div style={{
                  width: '24px', height: '24px', border: '4px solid #f59e0b',
                  borderTopColor: 'transparent', borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ fontSize: '22px', fontWeight: '700', color: '#92400e' }}>
                  Initializing cameras...
                </span>
              </>
            )}
          </div>

          {/* Capture Button */}
          <button
            onClick={handleCaptureAndProcess}
            disabled={!streamsReady || isProcessing}
            style={{
              padding: '32px', borderRadius: '20px', border: 'none',
              background: (!streamsReady || isProcessing) ? '#94a3b8' : '#22c55e',
              cursor: (!streamsReady || isProcessing) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px',
              boxShadow: streamsReady ? '0 8px 32px rgba(34,197,94,0.4)' : 'none',
              transition: 'all 0.2s'
            }}
            data-testid="capture-button"
          >
            {isProcessing ? (
              <>
                <div style={{
                  width: '36px', height: '36px', border: '5px solid white',
                  borderTopColor: 'transparent', borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ fontSize: '28px', fontWeight: '800', color: 'white' }}>
                  Processing... {ocrProgress}%
                </span>
              </>
            ) : (
              <>
                <Camera size={36} color="white" />
                <span style={{ fontSize: '28px', fontWeight: '800', color: 'white' }}>
                  Capture Both Sides
                </span>
              </>
            )}
          </button>

          {/* Retry Button */}
          {(frontStatus === 'error' || rearStatus === 'error') && (
            <button
              onClick={openBothStreams}
              style={{
                padding: '20px', borderRadius: '16px', border: '3px solid #3b82f6',
                background: 'white', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px'
              }}
            >
              <RotateCcw size={28} color="#3b82f6" />
              <span style={{ fontSize: '22px', fontWeight: '700', color: '#3b82f6' }}>
                Retry Camera Connection
              </span>
            </button>
          )}
        </div>

        {/* Hidden canvases for capture */}
        <canvas ref={frontCanvasRef} style={{ display: 'none' }} />
        <canvas ref={rearCanvasRef} style={{ display: 'none' }} />

        {/* HELP INFO - In content area, no footer */}
        <div style={{
          margin: '16px 40px 24px',
          padding: '20px 32px',
          background: '#1e3a5f',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={() => navigate('/kiosk/badge-intro', { state: { registrationData } })}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '14px 24px', borderRadius: '12px',
              background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer'
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={28} color="white" />
            <span style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>Back</span>
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Phone size={24} color="#fbbf24" />
            <span style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>Need help? Call </span>
            <span style={{ color: '#fbbf24', fontSize: '22px', fontWeight: '700' }}>01292 833107</span>
          </div>
        </div>

        {/* CSS Animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </KioskViewport>
  );
}
